/**
 * Add or edit a company: who it is, where it operates (India / USA) and as
 * what (MNC, GCC… Level 1–3), its career page, and how often to check it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '@/api/client'
import type { Company, CompanyCategory, CompanyMarket, Market, Tier } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { ErrorBox, Label, Modal, Select } from '@/components/ui/Primitives'
import { errorMessage, toast } from '@/components/ui/Toast'
import { PreviewPanel } from '@/features/companies/PreviewPanel'
import {
  ATS_LABEL,
  CATEGORIES,
  CATEGORY_HELP,
  CATEGORY_LABEL,
  INTERVALS,
  MARKET_FLAG,
  MARKET_LABEL,
  TIER_HELP,
  cn,
} from '@/lib/utils'

interface PresenceState {
  on: boolean
  category: CompanyCategory
  tier: Tier | ''
}

function initialPresence(company: Company | undefined, market: Market): PresenceState {
  const m = company?.markets.find((x) => x.market === market)
  if (m) return { on: true, category: m.category, tier: m.tier ?? '' }
  return { on: !company && market === 'IN', category: market === 'IN' ? 'gcc' : 'mnc', tier: '' }
}

export function CompanyFormModal({ company, onClose }: { company?: Company; onClose: () => void }) {
  const editing = Boolean(company)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState(company?.name ?? '')
  const [website, setWebsite] = useState(company?.website ?? '')
  const [industry, setIndustry] = useState(company?.industry ?? '')
  const [hq, setHq] = useState(company?.headquarters ?? '')
  const [careerUrl, setCareerUrl] = useState('')
  const [interval, setInterval] = useState(company?.crawl_interval_hours ?? 24)
  const [enabled, setEnabled] = useState(company?.scheduler_enabled ?? true)
  const [presence, setPresence] = useState<Record<Market, PresenceState>>({
    IN: initialPresence(company, 'IN'),
    US: initialPresence(company, 'US'),
  })

  const discover = useMutation({
    mutationFn: () => api.discover(website.trim()),
    onSuccess: (d) => {
      if (d.careers_url) {
        setCareerUrl(d.careers_url)
        preview.reset()
        toast.success(`Found: ${d.how}`)
      } else toast.error('No careers page found on the website — paste the URL instead')
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const preview = useMutation({ mutationFn: () => api.preview(careerUrl.trim()) })

  const markets: CompanyMarket[] = (['IN', 'US'] as Market[])
    .filter((m) => presence[m].on)
    .map((m) => ({ market: m, category: presence[m].category, tier: presence[m].tier || null }))

  const save = useMutation({
    mutationFn: () => {
      const common = {
        name: name.trim(),
        website: website.trim() || null,
        industry: industry.trim() || null,
        headquarters: hq.trim() || null,
        markets,
        scheduler_enabled: enabled,
        crawl_interval_hours: interval,
      }
      return company
        ? api.updateCompany(company.id, common)
        : api.createCompany({ ...common, career_urls: careerUrl.trim() ? [careerUrl.trim()] : [] })
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ['companies'] })
      void qc.invalidateQueries({ queryKey: ['stats'] })
      toast.success(editing ? 'Saved' : careerUrl ? `${saved.name} added — first check starting` : `${saved.name} added`)
      onClose()
      if (!editing) navigate(`/companies/${saved.id}`)
    },
    onError: (e) => toast.error(errorMessage(e)),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!markets.length) return toast.error('Pick India, USA or both')
    save.mutate()
  }

  return (
    <Modal title={editing ? `Edit ${company?.name}` : 'Add company'} onClose={onClose} width="max-w-3xl">
      <form onSubmit={submit} className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <Label>Company name</Label>
            <input className="field-box" value={name} onChange={(e) => setName(e.target.value)} required autoFocus placeholder="e.g. Walmart Global Tech" />
          </label>
          <label className="block">
            <Label hint="optional">Industry</Label>
            <input className="field-box" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="e.g. Banking, Software, Retail" />
          </label>
          <label className="block">
            <Label hint="optional">Website</Label>
            <input className="field-box" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://company.com" />
          </label>
          <label className="block">
            <Label hint="optional">Headquarters (country)</Label>
            <input className="field-box" value={hq} onChange={(e) => setHq(e.target.value)} placeholder="e.g. United States" />
          </label>
        </div>

        <div>
          <Label hint="category and level can differ per country">Where the company hires</Label>
          <div className="grid gap-3 sm:grid-cols-2">
            {(['IN', 'US'] as Market[]).map((m) => {
              const p = presence[m]
              const set = (patch: Partial<PresenceState>) => setPresence((prev) => ({ ...prev, [m]: { ...prev[m], ...patch } }))
              return (
                <div key={m} className={cn('rounded-field border p-3.5 transition', p.on ? 'border-ink-soft/40 bg-surface' : 'border-line bg-surface-muted/60')}>
                  <label className="flex cursor-pointer items-center gap-2 text-[14px] font-medium">
                    <input type="checkbox" checked={p.on} onChange={(e) => set({ on: e.target.checked })} className="h-4 w-4 accent-ink" />
                    {MARKET_FLAG[m]} {MARKET_LABEL[m]}
                  </label>
                  {p.on && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <Select value={p.category} onChange={(e) => set({ category: e.target.value as CompanyCategory })} title={CATEGORY_HELP[p.category]}>
                        {CATEGORIES.filter((c) => !(m === 'US' && c === 'gcc')).map((c) => (
                          <option key={c} value={c}>
                            {CATEGORY_LABEL[c]}
                          </option>
                        ))}
                      </Select>
                      <Select value={p.tier} onChange={(e) => set({ tier: (e.target.value ? Number(e.target.value) : '') as Tier | '' })}>
                        <option value="">Level —</option>
                        {[1, 2, 3].map((t) => (
                          <option key={t} value={t} title={TIER_HELP[t]}>
                            Level {t}
                          </option>
                        ))}
                      </Select>
                      <p className="col-span-2 text-[12px] leading-snug text-ink-muted">{CATEGORY_HELP[p.category]}</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {!editing && (
          <div>
            <Label hint="add more later on the company page">Career page URL</Label>
            <div className="flex gap-2">
              <input
                className="field-box"
                value={careerUrl}
                onChange={(e) => {
                  setCareerUrl(e.target.value)
                  preview.reset()
                }}
                placeholder="https://careers.company.com  ·  https://company.wd5.myworkdayjobs.com/External"
              />
              <Button variant="secondary" disabled={!website.trim()} loading={discover.isPending} onClick={() => discover.mutate()} title="Look for the careers page on the website">
                Find from website
              </Button>
              <Button variant="secondary" disabled={!careerUrl.trim()} loading={preview.isPending} onClick={() => preview.mutate()}>
                Test
              </Button>
            </div>
            {discover.data?.careers_url && (
              <p className="mt-1.5 text-[12.5px] text-ink-muted">
                {discover.data.ats_type ? `${ATS_LABEL[discover.data.ats_type]} · ` : ''}
                {discover.data.how}
              </p>
            )}
            {preview.isError && <div className="mt-3"><ErrorBox error={preview.error} /></div>}
            {preview.data && <div className="mt-3"><PreviewPanel preview={preview.data} /></div>}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <Label>Check for new openings</Label>
            <Select value={interval} onChange={(e) => setInterval(Number(e.target.value))} className="w-full">
              {INTERVALS.map(([h, label]) => (
                <option key={h} value={h}>
                  {label}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex items-end gap-2 pb-2 text-[14px]">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-ink" />
            Scheduler on
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
            {editing ? 'Save' : 'Add company'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
